import { useTranslation } from 'react-i18next';

import type { ArmContentAttributes } from '../../types/experiment-draft.types';
import { ENRICHED_ARMS } from '../../utils/arm-content-attributes';

interface Props {
  /** The single / union selection — shown flat when there's no per-arm map. */
  attributes: readonly string[];
  /** Per-arm selection; `null` for runs saved with a single selection. */
  byArm: ArmContentAttributes | null | undefined;
  /** E2E `data-testid` root; arms derive `${testId}-arm-{arm}` and chips `${testId}-chip-{attr}`. */
  testId?: string;
}

/**
 * Content-attribute chips for the details / restart sheets. Lists each enriched
 * arm's own attributes when the run carries a per-arm map, else the flat list —
 * so single-selection (legacy) runs render exactly as before.
 */
export function ContentAttributeChips({
  attributes,
  byArm,
  testId = 'content-attribute-chips',
}: Props) {
  const { t } = useTranslation('experiments');
  const arms = ENRICHED_ARMS.filter((arm) => (byArm?.[arm]?.length ?? 0) > 0);

  if (arms.length === 0) return <ChipList testId={testId} values={attributes} />;

  return (
    <div data-testid={testId} className="flex flex-col gap-2.5">
      {arms.map((arm) => (
        <div key={arm} data-testid={`${testId}-arm-${arm}`} className="flex items-start gap-2">
          <span
            data-testid={`${testId}-arm-label-${arm}`}
            className="bg-accent-100/60 text-accent-main flex h-5 min-w-5 shrink-0 items-center justify-center rounded px-1 text-[11px] font-semibold"
            title={t('detailsSheet.fields.contentAttributesArm', { arm })}
            aria-label={t('detailsSheet.fields.contentAttributesArm', { arm })}
          >
            {arm}
          </span>
          <ChipList testId={`${testId}-arm-${arm}`} values={byArm?.[arm] ?? []} />
        </div>
      ))}
    </div>
  );
}

/** Wrapping row of monospace attribute-key chips. */
function ChipList({ testId, values }: { testId: string; values: readonly string[] }) {
  return (
    <div data-testid={`${testId}-list`} className="flex flex-wrap gap-1.5">
      {values.map((attr) => (
        <span
          key={attr}
          data-testid={`${testId}-chip-${attr}`}
          className="border-accent-main/20 bg-accent-100 text-accent-main rounded-md border px-2 py-0.5 font-mono text-xs"
        >
          {attr}
        </span>
      ))}
    </div>
  );
}
